export interface SnCitizenServiceGeometry {
  type: 'Polygon'; // As of 1/21/25, only Polygon is supported.
  coordinates: number[][][];
}

interface SnBaseCitizenServiceParams {
  /** Offset in the records being returned compared to the total records. Defaults to 100. Should be incremented by the number of records returned (100) until all hits have been returned. */
  from?: number;
  /** Page size. Default 100. */
  size?: number;
  /** Sort videos 0 for descending, 1 ascending. Defaults to 0. */
  sort?: 0 | 1;
  /** Add better counts and remove blocked, private, and deleted videos. */
  enrich?: boolean;
}

export interface SnCitizenServiceGetVideosByIncidentIdParams extends SnBaseCitizenServiceParams {
  /** The incident ID to get videos for. */
  incidentId: string;
}

export interface SnCitizenServiceGetVideosByGeographyParams extends SnBaseCitizenServiceParams {
  /** An RFC7946 GeoJSON Geometry object (See: https://datatracker.ietf.org/doc/html/rfc7946#section-3.1). "Type" must be "Polygon". Area covered by this polygon will be included in the search results. */
  geometry: SnCitizenServiceGeometry;
  /** ISO8601 format including date and time in UTC. All videos created between this and the end time below will be included. */
  startDatetime?: Date;
  /** ISO8601 format including date and time in UTC. */
  endDatetime?: Date;
}

export interface SnCitizenServiceIncidentCreationParams {
  title: string;
  body: string;
  description?: string;
  location: { longitude: number; latitude: number };
  radius_miles?: number;
  geometries?: GeoJSON.Geometry[];
  agency_meta_data: SnCitizenServiceAgencyMetaData;
  media_url?: string;
}

export interface SnCitizenServiceAgencyMetaData {
  id?: string;
  name: string;
  logo?: string;
}

export interface SnCitizenServiceIncidentUpdateParams {
  agency_id: string;
  title: string;
  body: string;
  description?: string;
  notification_targets?: (
    | NotificationTargetsWithLocationAndRadiusMiles
    | NotificationTargetsWithGeometries
  )[];
  media_url?: string;
}

export interface NotificationTargetsParams {
  location?: { longitude: number; latitude: number };
  radius_miles?: number;
  geometries?: GeoJSON.Geometry[];
}

export type NotificationTargetsWithLocationAndRadiusMiles = Omit<
  NotificationTargetsParams,
  'location' | 'radius_miles' | 'geometries'
> & {
  location: { longitude: number; latitude: number };
  radius_miles: number;
  geometries?: never;
};

export type NotificationTargetsWithGeometries = Omit<
  NotificationTargetsParams,
  'location' | 'radius_miles' | 'geometries'
> & {
  geometries: GeoJSON.Geometry[];
  radius_miles?: never;
  location?: never;
};

export type IncidentCreationWithRadiusMiles = Omit<
  SnCitizenServiceIncidentCreationParams,
  'geometries' | 'radius_miles'
> & {
  radius_miles: number;
  geometries?: never;
};

export type IncidentCreationWithGeometries = Omit<
  SnCitizenServiceIncidentCreationParams,
  'radius_miles' | 'geometries'
> & {
  geometries: GeoJSON.Geometry[];
  radius_miles?: null;
};

export type CreateIncidentPayload =
  | IncidentCreationWithRadiusMiles
  | IncidentCreationWithGeometries;

export type UpdateIncidentPayload = SnCitizenServiceIncidentUpdateParams;

export type CitizenMetadata = {
  incidentNotificationCreationEnabled?: boolean;
  citizenIncidentServiceEnabled?: boolean;
};

export type MediaUploadResponse = {
  accessUrl: string;
  uploadUrl: string;
  fields: {
    key: string;
    policy: string;
    'x-google-algorithm': string;
    'x-google-credential': string;
    'x-google-date': string;
    'x-google-expires': string;
    'x-google-signature': string;
  };
};
